import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  IconButton,
  Collapse,
  Divider,
  useMediaQuery
} from '@mui/material';
import { ExpandLess, ExpandMore, Check, Add } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useTheme } from '@mui/material/styles';
import { INavigationHierarchy } from '../../../../../models';

interface INavigationItem {
  node: INavigationHierarchy;
  assignedIds: number[];
  onToggle: (
    node: INavigationHierarchy,
    newAssigned: boolean,
    parentChain?: INavigationHierarchy[]
  ) => void;
  level?: number;
  parentChain?: INavigationHierarchy[];
}

export const NavigationItem: React.FC<INavigationItem> = ({
  node,
  assignedIds,
  onToggle,
  level = 0,
  parentChain = []
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:960px)');
  const paddingLeft = theme.spacing((level + 1) * 2);
  const isAssigned = assignedIds.includes(node.navigationId);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Pass the full chain of parent nodes along with the node.
    onToggle(node, !isAssigned, parentChain);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(prev => !prev);
  };

  return (
    <>
      <ListItem
        style={{ paddingLeft }}
        className={classes.listItem}
        onClick={
          node.childNavigations && node.childNavigations.length > 0 ? handleExpandClick : undefined
        }
      >
        {node.childNavigations && node.childNavigations.length > 0 && (
          <IconButton onClick={handleExpandClick} sx={{ padding: 0 }} size="large">
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
        <ListItemText primary={node.name} secondary={node.hierarchicalName} />
        <ListItemSecondaryAction className={classes.buttonWrapper}>
          <Button
            startIcon={isAssigned ? <Check /> : <Add />}
            color="primary"
            variant={isAssigned ? 'contained' : 'text'}
            className={
              node.childNavigations && node.childNavigations.length > 0
                ? clsx(classes.editButton, isAssigned && classes.selected)
                : isAssigned
                ? classes.selected
                : undefined
            }
            onClick={handleToggle}
          >
            {isMobile ? '' : isAssigned ? 'Assigned' : 'Assign'}
          </Button>
        </ListItemSecondaryAction>
      </ListItem>
      <Divider />
      {node.childNavigations && node.childNavigations.length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {node.childNavigations.map(child => (
              <NavigationItem
                key={child.navigationId}
                node={child}
                assignedIds={assignedIds}
                onToggle={onToggle}
                level={level + 1}
                parentChain={[...parentChain, node]}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  listItem: {
    paddingTop: 0.25,
    paddingBottom: 0.25,
    paddingLeft: theme.spacing(1)
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    marginRight: 8,
    padding: 0
  },
  selected: {
    fontWeight: 'bold',
    padding: '2px 8px',
    borderRadius: 5,
    marginRight: 0
  }
}));
